import { Box, Text } from "@chakra-ui/react";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ProcessStepBar } from "../../../../common/layout/v2/ProcessStepBar";
import { EventHeader } from "./layout/EventHeader";
import { MobileStepDrawer } from "./layout/MobileStepDrawer";
import { AwardCriteriaStep } from "./steps/AwardCriteriaStep";
import { ClassificationStep } from "./steps/ClassificationStep";
import { DocumentsStep } from "./steps/DocumentsStep";
import { InfoStep } from "./steps/InfoStep";
import { MinimumRequirementsStep } from "./steps/MinimumRequirementsStep";
import { ProcedureStep } from "./steps/ProcedureStep";
import { QualificationStep } from "./steps/QualificationStep";
import { SummaryStep } from "./steps/SummaryStep";
import { DetailsStep } from "./steps/private/DetailsStep";
import { InvitationsStep } from "./steps/private/InvitationsStep";
import { ProductsStep } from "./steps/private/ProductsStep";
import { ReviewAndPublishStep } from "./steps/private/ReviewAndPublishStep";
import {
  bffApi,
  useConfirmSourcingEventClassificationMutation,
  useGetSourcingEventReadinessQuery,
  useUpdateSourcingEventMutation,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../autogen/bff-api";
import { useStepNavigation } from "./hooks/useStepNavigation";
import {
  resolveProcess,
  stepNeighbors,
  useStepLabel,
  useStepShortLabel,
  WIZARD_STEPS,
  type OrgWizardAccess,
  type PublicEventStep,
} from "./wizardSteps";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
  useEntityState,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../../../common/subscription/useSub";
import { ChangeResponsiblePersonButton } from "../../../v2/sourcing-event/ChangeResponsiblePersonButton";
import { saveWithVersionConflictRetry } from "./saveWithVersionConflictRetry";

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  refetch: () => void;
}

const INFO_STEP = WIZARD_STEPS[0];

export const EditSourcingEventWizardForm = ({ eventId, event, refetch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const editorErrorConfig = useApiErrorEditorConfig();
  const { trigger, triggerSoft } = useApiError();
  const [updateEvent] = useUpdateSourcingEventMutation();
  const [confirmClassification, { isLoading: isConfirming }] = useConfirmSourcingEventClassificationMutation();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const sub = useSub();
  const stepLabel = useStepLabel();
  const stepShortLabel = useStepShortLabel();
  const topRef = useRef<HTMLDivElement>(null);

  // Subscribed at the orchestrator level so the readiness cache survives
  // step navigation; sections re-subscribe via useReadinessByField and RTK
  // Query dedupes. Surface fetch failures via the soft toast — without this
  // the badges silently disappear and SummaryStep's Publish gate engages
  // with no user-visible reason.
  const { error: readinessError } = useGetSourcingEventReadinessQuery({ eventId });
  useEffect(() => {
    if (readinessError) triggerSoft(readinessError);
  }, [readinessError, triggerSoft]);

  const config = useMemo(
    () => ({
      initialState: event,
      save: async (fields: Partial<SourcingEventV2PatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateEvent({
              eventId,
              sourcingEventV2PatchDto: retryFields as SourcingEventV2PatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getSourcingEvent.initiate(
                { eventId },
                { forceRefetch: true },
              ),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [dispatch, event, eventId, updateEvent, editorErrorConfig],
  );

  useCreateEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId, config);

  const optimisticEvent = useEntityState<SourcingEventV2Dto>(eventId) ?? event;
  const access: OrgWizardAccess = {
    hasPublicProcurementAccess: sub.hasSourcingEventV2Access,
    hasProductAccess: sub.hasProductAccess,
  };
  const process = resolveProcess(optimisticEvent.eventContext.type, access);
  const steps = process?.steps ?? [INFO_STEP];
  const { currentStep, navigateToStep } = useStepNavigation(eventId, steps);

  // Reset scroll on step change; AppShellV2 scrolls an inner <main>, not the window.
  useEffect(() => {
    const scroller = topRef.current?.closest("main");
    (scroller ?? window).scrollTo({ top: 0 });
  }, [currentStep]);

  const confirmAndContinue = async () => {
    await coordinatorRegistry.flush(eventId);
    try {
      const confirmed = await confirmClassification({ eventId }).unwrap();
      coordinatorRegistry.get(eventId)?.setServerState(confirmed);
      const nextKey = resolveProcess(confirmed.eventContext.type, access)?.steps[1]?.key;
      if (nextKey) navigateToStep(nextKey);
    } catch (e) {
      trigger(e);
    }
  };

  const goTo = (step: PublicEventStep | undefined): (() => void) | undefined =>
    step ? () => navigateToStep(step) : undefined;
  const navPropsFor = (step: PublicEventStep) => {
    if (step === "info") {
      const { next } = process ? stepNeighbors("info", process.steps) : { next: undefined };
      return {
        nextStep: confirmAndContinue,
        nextStepLabel: next ? stepLabel(next) : t("Continue"),
        nextIsAction: !next,
        isNextLoading: isConfirming,
      };
    }
    const { previous, next } = stepNeighbors(step, steps);
    return { previousStep: goTo(previous), nextStep: goTo(next), nextStepLabel: next ? stepLabel(next) : undefined };
  };

  const stepRenderers: Record<PublicEventStep, () => ReactNode> = {
    info: () => <InfoStep eventId={eventId} {...navPropsFor("info")} />,
    classification: () => <ClassificationStep eventId={eventId} {...navPropsFor("classification")} />,
    procedure: () => <ProcedureStep eventId={eventId} {...navPropsFor("procedure")} />,
    qualification: () => (
      <QualificationStep
        eventId={eventId}
        isActive={currentStep === "qualification"}
        {...navPropsFor("qualification")}
      />
    ),
    awardCriteria: () => (
      <AwardCriteriaStep
        eventId={eventId}
        isActive={currentStep === "awardCriteria"}
        {...navPropsFor("awardCriteria")}
      />
    ),
    minimumRequirements: () => (
      <MinimumRequirementsStep
        eventId={eventId}
        isActive={currentStep === "minimumRequirements"}
        {...navPropsFor("minimumRequirements")}
      />
    ),
    documents: () => (
      <DocumentsStep
        eventId={eventId}
        event={event}
        refetch={refetch}
        isActive={currentStep === "documents"}
        {...navPropsFor("documents")}
      />
    ),
    summary: () => (
      <SummaryStep
        eventId={eventId}
        organization={selectedOrg}
        currentEditor={me}
        responsibleBuyer={optimisticEvent.responsiblePerson}
        responsibleEditControl={
          <ChangeResponsiblePersonButton
            eventId={eventId}
            ownerOrgId={selectedOrg.id}
            currentResponsible={optimisticEvent.responsiblePerson}
          />
        }
        previousStep={navPropsFor("summary").previousStep}
        navigateToStep={navigateToStep}
      />
    ),
    details: () => <DetailsStep eventId={eventId} {...navPropsFor("details")} />,
    products: () => <ProductsStep eventId={eventId} {...navPropsFor("products")} />,
    invitations: () => <InvitationsStep eventId={eventId} {...navPropsFor("invitations")} />,
    reviewAndPublish: () => (
      <ReviewAndPublishStep eventId={eventId} previousStep={navPropsFor("reviewAndPublish").previousStep} />
    ),
  };

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <Box ref={topRef} width="full" maxWidth="full">
      {process && <MobileStepDrawer steps={process.steps} currentStep={currentStep} navigateToStep={navigateToStep} />}
      <EventHeader title={optimisticEvent.eventData.title} modifiedAt={optimisticEvent.modifiedAt} />
      {process && (
        <Box display={{ base: "none", md: "block" }} py="8" overflowX="auto">
          <ProcessStepBar
            maxW="full"
            steps={process.steps.map((s) => ({ key: s.key, label: stepShortLabel(s.key) }))}
            currentKey={currentStep}
            onStepClick={(key) => navigateToStep(key as PublicEventStep)}
          />
        </Box>
      )}
      {process && (
        <Text display={{ base: "block", md: "none" }} py="4" fontSize="sm" fontWeight="semibold" color="smPrimary">
          {t("Step")} {currentIndex + 1}/{steps.length} · {stepLabel(currentStep)}
        </Text>
      )}
      <Box maxWidth="full" overflowX="hidden" pt={process ? undefined : "6"} pb={{ base: "20", md: "0" }}>
        {steps.map(({ key }) => (
          <Box key={key} display={currentStep === key ? undefined : "none"}>
            {stepRenderers[key]()}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
